import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormGroup, UntypedFormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { S3UrlService } from 'src/app/shared/services/s3-url.service';
import { CuentaBeneficiario } from 'src/app/dashboard/interfaces/cuenta-beneficiario.interface';
import { TransactionsAcServiceService } from 'src/app/retail/services/transactions-ac-service.service';
import { ErrorTypes } from 'src/app/shared/enums/error-types.enum';
import { TipoPago } from 'src/app/shared/enums/tipo-pago.enum';
import { ActionsService } from 'src/app/shared/services/actions.service';
import { SpinnerService } from 'src/app/shared/services/spinner.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-summary-order-fiat',
  templateUrl: './summary-order-fiat.component.html',
  styleUrls: ['./summary-order-fiat.component.sass'],
})
export class SummaryOrderFiatComponent {
  constructor(
    private spinnerService: SpinnerService,
    private transaccionService: TransactionsAcServiceService,
    private router: Router,
    private actionService: ActionsService,
    private fb: UntypedFormBuilder,
    private s3UrlService: S3UrlService,
  ) {
    this.datosForm = this.fb.group({
      photo: [],
    });
  }
  contentType = 'image/jpeg';
  fileExt = 'jpeg';
  comprobante: any;
  datosForm: FormGroup | undefined;
  @Input()
  public clpCurrency: number = 0;

  @Input()
  public vesCurrency: number = 0;

  @Input()
  public ves_clp_tax: number = 0;

  @Input()
  public valorCambioId: number = 0;

  @Input()
  public beneficiario: CuentaBeneficiario | undefined;

  @Output()
  public goBack = new EventEmitter<any>();
  setFileData(event: Event): void {
    const eventTarget: HTMLInputElement | null =
      event.target as HTMLInputElement | null;
    if (eventTarget?.files?.[0]) {
      const file: File = eventTarget.files[0];
      this.contentType = file.type;
      const filename = file.name;
      this.fileExt = filename.split('.').pop() || '';
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        this.datosForm?.get('photo')?.setValue(reader.result as string);
      });

      reader.readAsDataURL(file);
    }
  }

  /**
   * Post-VULN-01: backend firma la upload URL; browser hace PUT directo.
   *
   * Cajero retail sube comprobante de transferencia. Mismo `purpose:
   * 'comprobante'` que el modal FIAT del dashboard — folder compartido
   * `comprobantes/` sin subcarpeta de usuario. Key:
   *
   *    comprobantes/retail-<beneficiarioUserId>-<timestamp>.<ext>
   *
   * Retail no tiene un campo de "referencia" operativo, así que el
   * `suggestedName` se compone sintéticamente con el `userId` del
   * beneficiario + timestamp. La unicidad la garantiza el timestamp
   * (colisión al milisegundo es virtualmente imposible) y la
   * vinculación al cliente queda en BD vía la columna `voucher` /
   * `transaccion.comprobante` que se persiste al crear la transacción.
   *
   * Se envía `onBehalfOf: { userId }` opcional para que backend
   * registre auditoría del cliente vinculado (no afecta la key
   * resultante, por contrato del endpoint).
   */
  async uploadBase64Image(base64Image: string): Promise<void> {
    const beneficiarioUserId = this.beneficiario?.usuarioID;
    if (!beneficiarioUserId) {
      throw new Error('Beneficiario sin usuarioID — no se puede subir comprobante');
    }

    const cleaned = base64Image.replace(/^data:image\/\w+;base64,/, '');
    const binary = atob(cleaned);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    const blob = new Blob([bytes], { type: this.contentType });

    // suggestedName sintético — retail no tiene referencia operativa.
    // El front sanitiza con la misma regla que el dashboard FIAT.
    const synthRef = `retail-${beneficiarioUserId}-${new Date().getTime()}`;
    const safeRef = synthRef.replace(/[^a-zA-Z0-9_-]/g, '_');
    const suggestedName = `${safeRef}.${this.fileExt}`;

    const { url, key } = await this.s3UrlService.getUploadUrl(
      this.contentType,
      'comprobante',
      suggestedName,
      { userId: beneficiarioUserId },
    );
    const putRes = await fetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': this.contentType },
      body: blob,
    });
    if (!putRes.ok) {
      throw new Error(`S3 upload failed con HTTP ${putRes.status}`);
    }
    this.comprobante = key;
  }

  async confirmarEnvio() {
    const ref = this.spinnerService.start();
    let comprobante = undefined;
    if (
      this.datosForm?.contains('photo') &&
      !this.datosForm?.get('photo')?.value
    ) {
      Swal.fire(
        'Error al enviar dinero',
        'Debe subir un comprobante de pago',
        'warning'
      );
      this.spinnerService.stop(ref);
      return;
    }
    const response = await this.uploadBase64Image(
      this.datosForm?.get('photo')?.value
    );
    comprobante = this.comprobante;
    const transaccionDTO = {
      paisOrigen: 'CL',
      userId: this.beneficiario?.usuarioID,
      totalMonedaOrigen: +this.clpCurrency,
      totalMonedaDestino: +this.vesCurrency,
      monedaOrigen: 'CLP',
      monedaDestino: 'VES',
      paisDestino: 'VE',
      formaPago: TipoPago.TRANSFERENCIA,
      diferencia: null,
      tasa: +this.vesCurrency / +this.clpCurrency,
      cuentaBeneficiarioId: this.beneficiario?.cuentaBeneficiarioID,
      tasaCambioId: null,
      valorCambioId: this.valorCambioId!,
      tipoTransaccion: 'AC',
      voucher: comprobante,
    };

    try {
      const resultadoTransaccion =
        await this.transaccionService.createTransactionsAc(transaccionDTO);
      this.spinnerService.stop(ref);
      Swal.fire(
        'Transaccion creada',
        `Se ha creado la transaccion con éxito`,
        'success'
      );
      this.router.navigate(['/retail/home']);
    } catch (error: any) {
      if (
        error.status === 400 &&
        error.error.type === ErrorTypes.TASAS_ACTUALIZADAS
      ) {
        this.spinnerService.stop(ref);
        this.actionService.emitirAccionTasaUSDT();
      } else {
        Swal.fire(
          'Error al enviar dinero',
          'Ha ocurrido un error al realizar la transferencia',
          'warning'
        ).then(() => {
          this.spinnerService.stop(ref);
          this.router.navigate(['/retail/home']);
        });
      }
    }
  }

  goToBack(): void {
    this.goBack.emit();
  }
}
